// @ts-ignore
import traverseISODate from '@segment/isodate-traverse';

import { type Context } from '../types';

import { type SegmentContextDef } from './types';

export const buildContext = (context: Context): SegmentContextDef => {
	return {
		...context.context,
		userAgent: navigator.userAgent,
	};
};

export const prepareEventProperties = <T extends object>(properties: T): T => {
	const propertiesWithoutUndefined = nestedRemoveUndefinedValues(properties, '$.properties');
	return traverseISODate(propertiesWithoutUndefined);
};

// This function will essentially deepClone as well
const nestedRemoveUndefinedValues = <T extends object>(obj: T, path: string): T => {
	const recursionDepthLimit = 512;

	const nestedRemoveUndefinedValuesImpl = (
		source: any,
		processedReferences: Map<object, string>,
		path: string,
		depth: number,
	) => {
		if (source === null || typeof source !== 'object') {
			return source;
		}

		// If the level of nesting is egregiously excessive we'll stop processing any further to
		// avoid crashing due to stack exhaustion, instead we return a note object.
		if (depth > recursionDepthLimit) {
			return {
				_depthLimitExceeded: true,
				message: `Object nesting depth limit exceeded at path '${path}' with depth '${depth}'`,
			};
		}

		// If this object has already been seen then processing it would create a circular reference,
		// instead we return a note object.
		if (processedReferences.has(source)) {
			const originalPath = processedReferences.get(source);
			return {
				_circularReference: true,
				message: `Circular reference to object at original path '${originalPath}' detected at path '${path}'`,
				originalPath: originalPath,
			};
		}
		processedReferences.set(source, path);

		const clone: any = {};
		Object.keys(source).forEach((key) => {
			const value = source[key];
			if (value !== undefined) {
				if (Array.isArray(value)) {
					const newArray: any[] = [];
					value.forEach((v, i) => {
						const newValue = nestedRemoveUndefinedValuesImpl(
							v,
							processedReferences,
							`${path}.${key}[${i}]`,
							depth + 1,
						);
						if (newValue !== undefined) {
							newArray.push(newValue);
						}
					});
					clone[key] = newArray;
				} else {
					const newValue = nestedRemoveUndefinedValuesImpl(
						value,
						processedReferences,
						`${path}.${key}`,
						depth + 1,
					);
					if (newValue !== undefined) {
						clone[key] = newValue;
					}
				}
			}
		});

		processedReferences.delete(source);
		return clone;
	};

	return nestedRemoveUndefinedValuesImpl(obj, new Map<object, string>(), path, 0);
};
